import React from 'react';
import styled, { keyframes } from 'styled-components';
import { ProgressSpinnerSize } from './ProgressSpinner.types';

const ANIMATION_INTERVAL = 1300; // milliseconds

const SIZING = {
    small: 22,
    medium: 40,
    large: 60,
    xlarge: 138,
} as const;

const calcRadius = (size: ProgressSpinnerSize) => 9 * (SIZING[size] / 20.0);

const spin = keyframes`
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
`;

const oscillate = keyframes`
    from {
        transform: rotate(200deg);
    }
    to {
        transform: rotate(10deg);
    }
`;

const makeInitialArcGrowthIntro = keyframes`
    from {
        stroke-dashoffset: calc(var(--_Progress-spinner-pi) * var(--_Progress-spinner-radius) * (360 - 0) / 180.0);
    }
    to {
        stroke-dashoffset:  calc(var(--_Progress-spinner-pi) * var(--_Progress-spinner-radius) * (360 - var(--_Progress-spinner-arc-size-max)) / 180.0);
    }
`;

const makeGrowAndShrinkKeyframes = keyframes`
    from {
        stroke-dashoffset: calc(var(--_Progress-spinner-pi) * var(--_Progress-spinner-radius) * (360 - var(--_Progress-spinner-arc-size-min)) / 180.0);
    }
    to {
        stroke-dashoffset: calc(var(--_Progress-spinner-pi) * var(--_Progress-spinner-radius) * (360 - var(--_Progress-spinner-arc-size-max)) / 180.0);
    }
`;

export const getCircle = (size: ProgressSpinnerSize) => {
    const viewBoxSize = SIZING[size];
    return (
        <svg height={viewBoxSize} width={viewBoxSize} viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}>
            <circle
                className="arc"
                cx={viewBoxSize / 2}
                cy={viewBoxSize / 2}
                r={calcRadius(size)}
                fill="none"
                stroke="currentColor"
                strokeWidth={Math.min(viewBoxSize / 10, 7)}
            />
        </svg>
    );
};

export const StyledProgressSpinner = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    flex-direction: column;
    &[data-label-position='bottom'] .bbui-progress-spinner-label {
        margin-top: 0.75em;
    }
    &[data-label-position='right'] {
        flex-direction: row;
        .bbui-progress-spinner-label {
            margin-inline-start: 0.75em;
        }
    }
    .bbui-progress-spinner-b {
        color: var(--cds-progressspinner-color-background);
        position: absolute;
        top: 33px;
        margin: 0 auto;
    }

    .spinner {
        display: block;
        position: relative;
        overflow: hidden;

        width: var(--_Progress-spinner-width);
        height: var(--_Progress-spinner-height);

        .rotation {
            position: absolute;
            height: 100%;
            width: 100%;

            animation: ${spin} ${ANIMATION_INTERVAL}ms infinite linear;

            .oscillation {
                position: absolute;
                width: 100%;
                height: 100%;

                animation: ${oscillate} ${ANIMATION_INTERVAL}ms infinite ease-in-out;
                animation-direction: alternate;

                .arc {
                    stroke-dashoffset: calc(
                        var(--_Progress-spinner-pi) * var(--_Progress-spinner-radius) * (360 - 10) / 180
                    );
                    stroke-dasharray: calc(2 * var(--_Progress-spinner-pi) * var(--_Progress-spinner-radius));

                    animation-name: ${makeInitialArcGrowthIntro}, ${makeGrowAndShrinkKeyframes};
                    animation-duration: ${ANIMATION_INTERVAL}ms, ${ANIMATION_INTERVAL}ms;
                    animation-delay: 0, ${ANIMATION_INTERVAL}ms;
                    animation-timing-function: ease-in-out, ease-in-out;
                    animation-iteration-count: 1, infinite;
                    animation-direction: normal, alternate;
                    color: var(--cds-progressspinner-color-background);
                }
            }
        }
    }
`;
